import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@flote/ui';

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

/**
 * Error fallback component for use with SentryErrorBoundary.
 * Provides a user-friendly error UI with retry functionality.
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16">
      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
        <p className="mt-3 text-muted-foreground">
          We encountered an unexpected error. Please try refreshing the page or return to the
          home page.
        </p>
        {import.meta.env.DEV && errorMessage && (
          <div className="mt-4 rounded-md bg-muted p-4 text-left">
            <p className="text-xs font-mono text-destructive">{errorMessage}</p>
          </div>
        )}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={resetError} variant="default" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <Link to="/brands">
              <Home className="h-4 w-4" />
              Go to Home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
